<template>
    <section id="free-day-config" class="page-content">
        <header id="section-header">
            <p class="title has-text-grey">Free Days Config</p>
            <p class="subtitle has-text-grey">Edit, create and delete Free Days information.</p>
        </header>
        <main id="section-body">
            <div class="filters">
                <div class="filter">
                    <label>Carrier</label>
                    <div class="control">
                        <AutoComplete
                            v-model="selectedCarrierFilter"
                            :suggestions="filteredCarriersFilter"
                            @complete="searchCarrierFilter($event)"
                            @item-select="carrierSelectedFilter($event)"
                            @item-unselect="carrierUnselectedFilter($event)"
                            @clear="carrierUnselectedFilter($event)"
                            :dropdown="true"
                            field="official_name"
                        >
                            <template #item="slotProps">
                                <div class="item">
                                    <div>{{ slotProps.item.official_name }} - {{ slotProps.item.scac }}</div>
                                </div>
                            </template>
                        </AutoComplete>
                    </div>
                </div>
                <div class="filter">
                    <label>Country</label>
                    <div class="control">
                        <AutoComplete
                            v-model="selectedCountryFilter"
                            :suggestions="filteredCountriesFilter"
                            @complete="searchCountryFilter($event)"
                            @item-select="countrySelectedFilter($event)"
                            @item-unselect="countryUnselectedFilter($event)"
                            @clear="countryUnselectedFilter($event)"
                            :dropdown="true"
                            field="name"
                        >
                            <template #item="slotProps">
                                <div class="item">
                                    <div>{{ slotProps.item.name }} - {{ slotProps.item.code }}</div>
                                </div>
                            </template>
                        </AutoComplete>
                    </div>
                </div>
                <div class="filter">
                    <label>Port</label>
                    <div class="control">
                        <AutoComplete
                            v-model="selectedPortFilter"
                            :suggestions="filteredPortsFilter"
                            @complete="searchPortFilter($event)"
                            @item-select="portSelectedFilter($event)"
                            @item-unselect="portUnselectedFilter($event)"
                            @clear="portUnselectedFilter($event)"
                            :dropdown="true"
                            field="name"
                        >
                            <template #item="slotProps">
                                <div class="item">
                                    <div>{{ slotProps.item.name }} - {{ slotProps.item.locode }}</div>
                                </div>
                            </template>
                        </AutoComplete>
                    </div>
                </div>
                <div class="filter">
                    <label>Management</label>
                    <div class="control">
                        <button class="button is-primary is-small" @click="clearFilters()">Reset Filters</button>
                        <button class="button is-primary is-small" @click="showFreeDayDetail()">Add</button>
                    </div>
                </div>
            </div>
            <div>
                <data-table
                    :expand="['id']"
                    :objects="_objects"
                    :schema="schema"
                    :totalItems="count"
                    :ordering="orderingColumns"
                    @order="filters.ordering = $event; applyFilters()"
                    @expand="showFreeDayDetail($event)"
                >
                    <div slot-scope="actions">
                        <modal-confirm :disabled="false" @confirm="deleteFreeDay(actions.object)">
                            <div slot="title">Confirm Delete</div>
                            <span slot="body">Are you sure you want to <strong>delete</strong>?</span>
                            <div slot="footer">
                                <button class="button" @click="showModal = false; $emit('close')">
                                    No, I'm not
                                </button>
                                <button class="button is-primary" @click="showModal = false; $emit('confirm')">
                                    Yes, I'm sure
                                </button>
                            </div>
                            <div slot="trigger" class="button is-success is-small">Delete</div>
                        </modal-confirm>
                    </div>
                </data-table>
                <pagination :currentPage.sync="page" :totalItems="count" :itemsPerPage="pageSize" />

                <modal width="450px" v-if="showModal" @close="showModal=false">
                    <div slot="header" style="font-family: MuseoSans-700; color: #138366;font-size:1.4em">
                        Free Day Detail
                    </div>
                    <div slot="body">
                        <div id="edit-free-day-modal" class="field field-inline">
                            <label class="label">Carrier *</label>
                            <div class="control">
                                <AutoComplete
                                    v-if="!freeDay.id"
                                    id="carrier-autocomplete"
                                    v-model="selectedCarrier"
                                    :suggestions="filteredCarriers"
                                    @complete="searchCarrier($event)"
                                    @item-select="carrierSelected($event)"
                                    @item-unselect="carrierUnselected($event)"
                                    @clear="carrierUnselected($event)"
                                    :dropdown="true"
                                    field="official_name"
                                    forceSelection
                                    autoHighlight
                                >
                                    <template #item="slotProps">
                                        <div class="item">
                                            <div>{{ slotProps.item.official_name }} - {{ slotProps.item.scac }}</div>
                                        </div>
                                    </template>
                                </AutoComplete>
                                <span v-else>{{ freeDay.carrier.official_name }} - {{ freeDay.carrier.scac }}</span>
                            </div>
                        </div>
                        <div class="field field-inline">
                            <label class="label">Country *</label>
                            <div class="control">
                                <AutoComplete
                                    v-if="!freeDay.id"
                                    id="country-autocomplete"
                                    v-model="selectedCountry"
                                    :suggestions="filteredCountries"
                                    @complete="searchCountry($event)"
                                    @item-select="countrySelected($event)"
                                    @item-unselect="countryUnselected($event)"
                                    @clear="countryUnselected($event)"
                                    :dropdown="true"
                                    field="name"
                                    forceSelection
                                    autoHighlight
                                >
                                    <template #item="slotProps">
                                        <div class="item">
                                            <div>{{ slotProps.item.name }} - {{ slotProps.item.code }}</div>
                                        </div>
                                    </template>
                                </AutoComplete>
                                <span v-else>{{ freeDay.country.name }} - {{ freeDay.country.code }}</span>
                            </div>
                        </div>
                        <div class="field field-inline">
                            <label class="label">Port</label>
                            <div class="control">
                                <AutoComplete
                                    v-if="!freeDay.id"
                                    id="port-autocomplete"
                                    v-model="selectedPort"
                                    :suggestions="filteredPorts"
                                    @complete="searchPort($event)"
                                    @item-select="portSelected($event)"
                                    @item-unselect="portUnselected($event)"
                                    @clear="portUnselected($event)"
                                    :dropdown="true"
                                    field="locode"
                                    forceSelection
                                    autoHighlight
                                >
                                    <template #item="slotProps">
                                        <div class="item">
                                            <div>{{ slotProps.item.name }} - {{ slotProps.item.locode }}</div>
                                        </div>
                                    </template>
                                </AutoComplete>
                                <span v-else>{{ freeDay.port.name }} - {{ freeDay.port.locode }}</span>
                            </div>
                        </div>
                        <div class="field field-inline">
                            <label class="label">Storage Free Days</label>
                            <div class="control">
                                <input class="input" type="number" v-model="freeDay.storage_free_days">
                            </div>
                        </div>
                        <div class="field field-inline">
                            <label class="label">Demurrage Free Days</label>
                            <div class="control">
                                <input class="input" type="number" v-model="freeDay.demurrage_free_days">
                            </div>
                        </div>
                        <div class="field field-inline">
                            <label class="label">Detention Free Days</label>
                            <div class="control">
                                <input class="input" type="number" v-model="freeDay.detention_free_days">
                            </div>
                        </div>
                    </div>

                    <div slot="footer">
                        <button class="button is-primary" style="font-family: MuseoSans-900;text-transform: uppercase; border-radius: 6px;" @click="save()">
                            Save
                        </button>
                        <button class="button is-black" style="font-family: MuseoSans-900;text-transform: uppercase; border-radius: 6px;" @click="showModal=false">
                            Close
                        </button>
                    </div>
                </modal>
            </div>
        </main>
    </section>
</template>

<script>
import {mapActions, mapState, mapGetters} from 'vuex'
import TagInput from '@/components/TagInput'
import DataTable from '@/components/DataTable'
import Modal from '@/components/Modal'
import ModalConfirm from '@/components/ModalConfirm'
import PaginatedFilteredListMixin from '@/pages/mixins/PaginatedFilteredListMixin'
import Pagination from '@/components/Pagination'

import FreeDay from '@/models/freeDay'

export default {
  name: 'free-day-config',
  components: {TagInput, DataTable, Pagination, Modal, ModalConfirm},
  mounted () {
    this.$emit('loading')
    if (!this.hasPermission('canWrite')) this.$router.push('dashboard')
    const promises = [
      this.getPage(this.page)
    ]
    Promise.all(promises).then(data => { this.$emit('loaded') })
  },
  mixins: [PaginatedFilteredListMixin],
  data () {
    return {
      schema: FreeDay.schema(),
      objects: [],
      showModal: false,
      filters: {},
      orderingColumns: [
        'id',
        'carrier',
        'country',
        'port'
      ],
      carriersFilter: [],
      selectedCarrierFilter: null,
      filteredCarriersFilter: [],
      countriesFilter: [],
      selectedCountryFilter: null,
      filteredCountriesFilter: [],
      portsFilter: [],
      selectedPortFilter: null,
      filteredPortsFilter: [],
      freeDay: {},
      carriers: [],
      filteredCarriers: [],
      selectedCarrier: null,
      countries: [],
      filteredCountries: [],
      selectedCountry: null,
      ports: [],
      filteredPorts: [],
      selectedPort: null
    }
  },
  computed: {
    ...mapState({
      count: state => state.freeDay.count,
      pageSize: state => state.pageSize
    }),
    ...mapGetters({hasPermission: 'hasPermission'}),
    _objects: function () {
      var objects = JSON.parse(JSON.stringify(this.objects))
      return objects
    }
  },
  methods: {
    ...mapActions({
      getFreeDay: 'freeDay/retrieve',
      getObjects: 'freeDay/list',
      getCarriers: 'carrier/list',
      getPorts: 'port/list',
      getCountries: 'country/list',
      saveFreeDay: 'freeDay/save',
      deleteObject: 'freeDay/delete'
    }),
    showFreeDayDetail (freeDay) {
      this.getCarriers().then(response => {
        this.carriers = response
      })
      this.getCountries().then(response => {
        this.countries = response
      })
      this.getPorts().then(response => {
        this.ports = response
      })

      if (freeDay) {
        this.freeDay = freeDay
      } else {
        this.freeDay = {}
      }

      if (this.freeDay.id) {
        if (this.freeDay.carrier) {
          this.selectedCarrier = this.freeDay.carrier
        }
        if (this.freeDay.country) {
          this.selectedCountry = this.freeDay.country
        }
        if (this.freeDay.port) {
          this.selectedPort = this.freeDay.port
        }
      } else {
        this.freeDay = {}
        this.selectedCarrier = null
        this.selectedCountry = null
        this.selectedPort = null
      }

      this.showModal = true
    },

    reloadFilteredCarriersFilter () {
      this.filteredCarriersFilter = []
      this.carriersFilter.forEach(carrier => this.filteredCarriersFilter.push(carrier.toJSON()))
    },

    searchCarrierFilter (event) {
      let value = event.query

      let params = {}

      if (value.trim().length) {
        params.search = value
      }
      this.getCarriers(params).then(response => {
        this.carriersFilter = response
        this.reloadFilteredCarriersFilter()
      })
    },

    carrierSelectedFilter (event) {
      this.selectedCarrierFilter = event.value
      this.filters.carrier = this.selectedCarrierFilter.id
      this.applyFilters()
    },

    carrierUnselectedFilter (event) {
      this.selectedCarrierFilter = null
      delete this.filters.carrier
      this.applyFilters()
    },

    reloadFilteredCountriesFilter () {
      this.filteredCountriesFilter = []
      this.countriesFilter.forEach(country => this.filteredCountriesFilter.push(country.toJSON()))
    },

    searchCountryFilter (event) {
      let value = event.query

      let params = {}

      if (value.trim().length) {
        params.search = value
      }
      this.getCountries(params).then(response => {
        this.countriesFilter = response
        this.reloadFilteredCountriesFilter()
      })
    },

    countrySelectedFilter (event) {
      this.selectedCountryFilter = event.value
      this.filters.country = this.selectedCountryFilter.id
      this.applyFilters()
    },

    countryUnselectedFilter (event) {
      this.selectedCountryFilter = null
      delete this.filters.country
      this.applyFilters()
    },

    reloadFilteredPortsFilter () {
      this.filteredPortsFilter = []
      this.portsFilter.forEach(port => this.filteredPortsFilter.push(port.toJSON()))
    },

    searchPortFilter (event) {
      let value = event.query

      let params = {}

      if (value.trim().length) {
        params.search = value
      }

      if (this.selectedCountryFilter) {
        params.country = this.selectedCountryFilter.id
      }

      this.getPorts(params).then(response => {
        this.portsFilter = response
        this.reloadFilteredPortsFilter()
      })
    },

    portSelectedFilter (event) {
      this.selectedPortFilter = event.value
      this.filters.port = this.selectedPortFilter.id
      this.applyFilters()
    },

    portUnselectedFilter (event) {
      this.selectedPortFilter = null
      delete this.filters.port
      this.applyFilters()
    },

    clearFilters () {
      this.selectedCarrierFilter = null
      this.selectedCountryFilter = null
      this.selectedPortFilter = null
      this.filters = {}
      this.applyFilters()
    },

    reloadFilteredCarriers () {
      this.filteredCarriers = []
      this.carriers.forEach(carrier => this.filteredCarriers.push(carrier.toJSON()))
    },
    reloadFilteredCountries () {
      this.filteredCountries = []
      this.countries.forEach(country => this.filteredCountries.push(country.toJSON()))
    },
    reloadFilteredPorts () {
      this.filteredPorts = []
      this.ports.forEach(port => this.filteredPorts.push(port.toJSON()))
    },
    searchCarrier (event) {
      let value = event.query

      let params = {}

      if (value.trim().length) {
        params.search = value
      }
      this.getCarriers(params).then(response => {
        this.carriers = response
        this.reloadFilteredCarriers()
      })
    },
    searchCountry (event) {
      let value = event.query

      let params = {}

      if (value.trim().length) {
        params.search = value
      }
      this.getCountries(params).then(response => {
        this.countries = response
        this.reloadFilteredCountries()
      })
    },
    searchPort (event) {
      let value = event.query

      let params = {}

      if (value.trim().length) {
        params.search = value
      }

      if (this.selectedCountry) {
        params.country = this.selectedCountry.id
      }

      this.getPorts(params).then(response => {
        this.ports = response
        this.reloadFilteredPorts()
      })
    },

    carrierSelected (event) {
      this.selectedCarrier = event.value
      this.freeDay.carrier = this.selectedCarrier
    },
    carrierUnselected (event) {
      this.selectedCarrier = null
      this.freeDay.carrier = null
    },

    countrySelected (event) {
      this.selectedCountry = event.value
      this.freeDay.country = this.selectedCountry
    },

    countryUnselected (event) {
      this.selectedCountry = null
      this.freeDay.country = null
    },

    portSelected (event) {
      this.selectedPort = event.value
      this.freeDay.port = this.selectedPort
    },

    portUnselected (event) {
      this.selectedPort = null
      this.freeDay.port = null
    },

    save () {
      let data = {
        id: this.freeDay.id,
        carrier: this.freeDay.carrier.id,
        country: this.freeDay.country.id,
        port: null,
        storage_free_days: this.freeDay.storage_free_days,
        detention_free_days: this.freeDay.detention_free_days,
        demurrage_free_days: this.freeDay.demurrage_free_days
      }

      if (this.freeDay.port) {
        data.port = this.freeDay.port.id
      }

      this.saveFreeDay(data).then(response => {
        this.getPage(this.page)
        this.freeDay = {}
        this.selectedCarrier = null
        this.selectedCountry = null
        this.selectedPort = null
        this.showModal = false
      })
    },
    deleteFreeDay (freeDay) {
      this.deleteObject(freeDay).then(response => {
        this.getPage(this.page)
      })
    }
  }
}
</script>
<style lang="scss">
    li{
      padding: .2em 0;
    }
    .add-button {
      margin: .5em 0;
    }

    .field .p-autocomplete {
      width: 100%;
    }

    .modal-body {
      padding: 1em;
    }
</style>




// WEBPACK FOOTER //
// src/pages/PageFreeDaysConfig.vue